import { containsPrivateAnalyticsUrl, crossesWorkspaceBoundary, isPrivateWorkspaceUrl } from '@/lib/analytics-privacy'

// Analytics must not block hydration. `posthog-js` is ~400KB; importing it at
// module top-level pulls it into every page's initial bundle and runs during
// hydration. Instead we dynamically import + init once the browser is idle
// (or on first interaction), so first paint and interactivity stay fast.

const isDev = process.env.NODE_ENV === 'development'

// Whether the page is served from a local dev host. Localhost events used to be
// sent to the production PostHog project, inflating/distorting visitor
// analytics. We now skip capture on localhost by default; set
// NEXT_PUBLIC_POSTHOG_DEBUG=true to opt back in when you specifically want to
// test tracking locally (those events are tagged `environment: development`).
function isLocalhost(): boolean {
  const h = window.location.hostname
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '[::1]' ||
    h === '::1' ||
    h.endsWith('.local')
  )
}

const debugOptIn = process.env.NEXT_PUBLIC_POSTHOG_DEBUG === 'true'

function initPostHog() {
  if (isPrivateWorkspaceUrl(window.location.href)) return
  const local = isLocalhost()
  // Keep the production project clean: don't send localhost traffic unless
  // explicitly opted in for debugging.
  if (local && !debugOptIn) return

  const environment = local ? 'development' : 'production'

  import('posthog-js').then(({ default: posthog }) => {
    if (isPrivateWorkspaceUrl(window.location.href)) return
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_TOKEN!, {
      // Sent straight to PostHog (or a PostHog managed proxy set in this env),
      // never through our own domain: proxying via a Next.js rewrite made every
      // event batch and replay chunk a Vercel CDN request — over half of the
      // Hobby plan's 1M/month (Oct 2026).
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      ui_host: 'https://us.posthog.com',
      defaults: '2026-01-30',
      capture_exceptions: true,
      before_send: event => isPrivateWorkspaceUrl(window.location.href) || containsPrivateAnalyticsUrl(event?.properties) ? null : event,
      debug: isDev,
      // Localhost debug runs only need events: keep replays, heatmaps and
      // autocapture noise out of the production project.
      disable_session_recording: isDev,
      enable_heatmaps: isDev ? false : undefined,
      ...(isDev && {
        autocapture: false,
        capture_dead_clicks: false,
      }),
      loaded: (ph) => {
        ph.register({ environment })
      },
    })
  })
}

if (typeof window !== 'undefined') {
  // Cross the public/private boundary with a new document. This prevents an
  // already-loaded marketing tracker or replay script following a client-side
  // navigation into customer searches and financial screens.
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
    if (!(anchor instanceof HTMLAnchorElement) || anchor.download || (anchor.target && anchor.target !== '_self')) return
    if (!crossesWorkspaceBoundary(window.location.href, anchor.href)) return
    event.preventDefault(); event.stopImmediatePropagation(); window.location.assign(anchor.href)
  }, true)

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(initPostHog, { timeout: 4000 })
  } else if (document.readyState === 'complete') {
    // Safari has no requestIdleCallback — defer to after load instead.
    setTimeout(initPostHog, 1500)
  } else {
    window.addEventListener('load', () => setTimeout(initPostHog, 1500), {
      once: true,
    })
  }
}
